import { PropsWithChildren, useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Stack,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import HomeIcon from '@mui/icons-material/Home';
import BadgeIcon from '@mui/icons-material/Badge';
import AppsIcon from '@mui/icons-material/Apps';
import { Link, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { OidcProtectedComponent, useOidc } from '@nohara/react-oidc';
import { permission } from '@nohara/oidc-core';

import { LazyLayout } from '../ui';

import { UserSection } from '@/features/users/components/user-section';
import { paths } from '@/config/paths';
import { LanguageSwitcher } from '@/components/language';
import { ThemeToggle } from '@/components/theme';

const drawerWidth = 240;

const readAppsPermissions = [permission.app.read];
const readRolesPermissions = [permission.role.read];
const readUsersPermissions = [permission.user.read];
const readOrgsPermissions = [permission.org.read];

export const AdminLayout = ({ children }: PropsWithChildren) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { userId } = useOidc();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Stack height='100vh'>
      <Toolbar>
        <Typography variant='h6' noWrap component='div'>
          {t('navigation.adminTitle')}
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        <ListItem disablePadding component={Link} to={paths.org.select.getHref()} viewTransition>
          <ListItemButton>
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary={t('navigation.menu.home')} />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem disablePadding component={Link} to={paths.admin.root.path} viewTransition>
          <ListItemButton>
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary={t('navigation.menu.dashboard')} />
          </ListItemButton>
        </ListItem>
        <OidcProtectedComponent requireAll={readAppsPermissions}>
          <ListItem disablePadding component={Link} to={paths.admin.apps.path} viewTransition>
            <ListItemButton>
              <ListItemIcon>
                <AppsIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.menu.apps')} />
            </ListItemButton>
          </ListItem>
        </OidcProtectedComponent>
        <OidcProtectedComponent requireAll={readRolesPermissions}>
          <ListItem disablePadding component={Link} to={paths.admin.roles.path} viewTransition>
            <ListItemButton>
              <ListItemIcon>
                <BadgeIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.menu.roles')} />
            </ListItemButton>
          </ListItem>
        </OidcProtectedComponent>
        <OidcProtectedComponent requireAll={readUsersPermissions}>
          <ListItem disablePadding component={Link} to={paths.admin.users.path} viewTransition>
            <ListItemButton>
              <ListItemIcon>
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.menu.users')} />
            </ListItemButton>
          </ListItem>
        </OidcProtectedComponent>
        <OidcProtectedComponent requireAll={readOrgsPermissions}>
          <ListItem disablePadding component={Link} to={paths.admin.orgs.path} viewTransition>
            <ListItemButton>
              <ListItemIcon>
                <BusinessIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.menu.organizations')} />
            </ListItemButton>
          </ListItem>
        </OidcProtectedComponent>
      </List>
      <Stack flex={1} />
      <Divider />
      <UserSection
        userId={userId}
        profilePath={paths.admin.profile.path}
        logoutPath={paths.auth.login.path}
      />
    </Stack>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position='fixed'
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'error.main',
        }}
      >
        <Toolbar>
          <IconButton
            color='inherit'
            aria-label='open drawer'
            edge='start'
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant='h6' noWrap component='div' sx={{ flexGrow: 1 }}>
            {t('navigation.adminAppTitle')}
          </Typography>
          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }} gap={2}>
            <ThemeToggle />
            <LanguageSwitcher />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component='nav'
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label='mailbox folders'
      >
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant='permanent'
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component='main'
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        {(navigation.state === 'loading' && <LazyLayout />) || children}
      </Box>
    </Box>
  );
};
